import axios from 'axios'
import { logger } from '../utils/logger'
import { getTenantSlug, whenTenantResolved } from '../context/TenantContext'
import { reportError } from './errorReporter'
import { PATIENT_TOKEN_REQUIRED_EVENT } from '../utils/patientSession'
import type { ApiResponse } from '../types'

const API_URL = import.meta.env.VITE_API_URL || '/api'

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Patient session token (magic-link auth) — set on link exchange, cleared on reset.
// Kept module-level so the interceptor never needs to touch the store directly.
let patientSessionToken: string | null = null

export function setPatientSessionToken(token: string | null): void {
  patientSessionToken = token
}

// Add patient session token and tenant ID to requests
api.interceptors.request.use(async (config) => {
  // Never before the org is known: a request without X-Tenant-ID counts as 'de'
  await whenTenantResolved()
  if (patientSessionToken) {
    config.headers['X-Patient-Token'] = patientSessionToken
  }

  // Add tenant identifier for multi-tenancy
  const tenantSlug = getTenantSlug()
  if (tenantSlug) {
    config.headers['X-Tenant-ID'] = tenantSlug
  }

  return config
})

// Auto-report API errors (non-blocking)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // The org only admits patients with a magic-link session and this browser
    // has none (never opened a link, or the session expired). Every further call
    // would fail the same way, so tell the app once — it swaps the form for a
    // "please open your personal link" page. The saved progress stays intact.
    if (error.response?.data?.code === 'patient_token_required' && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(PATIENT_TOKEN_REQUIRED_EVENT))
    }

    // Don't report error-log endpoint failures (avoid loops)
    const url = error.config?.url || ''
    const status = error.response?.status
    const code = error.response?.data?.code
    // signing_busy / signing_temporarily_unavailable: expected, and the server already logged them
    if (!url.includes('error-logs') && status && (status >= 500 || status === 429) && code !== 'signing_busy' && code !== 'signing_temporarily_unavailable') {
      // Try to extract patientNumber from request body (JSON or FormData)
      let patientNumber: string | undefined
      try {
        const data = error.config?.data
        if (data instanceof FormData) {
          const v = data.get('patientNumber')
          patientNumber = typeof v === 'string' ? v : undefined
        } else if (typeof data === 'string') {
          patientNumber = JSON.parse(data).patientNumber
        } else if (data && typeof data === 'object') {
          patientNumber = (data as { patientNumber?: string }).patientNumber
        }
      } catch { /* ignore parse errors */ }
      // Fallback: extract from URL path (e.g., /yousign/store-signed/<id> uses patientId)
      if (!patientNumber) {
        const m = url.match(/\/(P\d{4,})\b/)
        if (m) patientNumber = m[1]
      }

      reportError({
        patientNumber,
        errorType: 'api_error',
        errorMessage: error.response?.data?.error || error.message || 'API error',
        endpoint: url,
        statusCode: error.response.status,
        metadata: {
          method: error.config?.method?.toUpperCase(),
          // Correlates this report with the backend log line / ErrorLog entry
          requestId: error.response?.headers?.['x-request-id'],
        },
      })
    }
    return Promise.reject(error)
  }
)

/** Machine-readable error code from a backend AppError response (if any) */
export function getApiErrorCode(error: unknown): string | undefined {
  const axiosErr = error as { response?: { data?: { code?: string } } }
  return axiosErr.response?.data?.code
}

/** Human-readable error message from a backend AppError response (if any) */
export function getApiErrorMessage(error: unknown): string | undefined {
  // Callers pass whatever they caught — axios errors, plain Errors, or nothing.
  const axiosErr = (error ?? {}) as { response?: { status?: number; data?: { error?: string; code?: string } } }
  const status = axiosErr.response?.status
  const msg = axiosErr.response?.data?.error
  // 503 signing_busy is deliberate backpressure with a patient-facing text ("try again in N minutes")
  if (axiosErr.response?.data?.code === 'signing_busy') return msg
  // 5xx bodies are for logs, not patients — return undefined so every caller
  // falls back to its localized "please try again" copy.
  if ((status !== undefined && status >= 500) || msg === 'Internal server error') return undefined
  return msg
}

/** Extra fields of an AppError response, e.g. the link's locale on a 410 */
export function getApiErrorDetails(error: unknown): Record<string, unknown> | undefined {
  const axiosErr = error as { response?: { data?: { details?: Record<string, unknown> } } }
  return axiosErr.response?.data?.details
}

/** HTTP status from an axios error (if any) */
export function getApiErrorStatus(error: unknown): number | undefined {
  const axiosErr = error as { response?: { status?: number } }
  return axiosErr.response?.status
}

// Patient magic links
/** What the exchange of a magic link returns; cohort/variant only for psychotherapy patients */
export interface PatientLinkExchange {
  patientNumber: string
  organizationSlug: string
  sessionToken: string
  draft?: Record<string, unknown>
  /** Admin test link: run in test mode */
  test?: boolean
  /** The patient completed the form recently: show the "already completed" page first */
  alreadyCompleted?: boolean
  cohort?: 'psychotherapy' | 'psychotherapy_returning'
  variant?: {
    /** The contract steps in signing order (server-decided) — statutory insured */
    contractKeys: string[]
    /** …per insurance type (public / private / none); absent from a server before 2026-10-29 */
    contractKeysByInsurance?: Record<string, string[]>
    /** Returning patients: what they signed before and their earlier answers */
    returning?: {
      privacySigned: boolean
      billingSigned: boolean
      hasMandate: boolean
      prefill: Record<string, unknown> | null
    }
  }
}

export const patientLinkService = {
  /**
   * Exchange a one-time link token (?ptk=...) for a patient session.
   * Throws on invalid/expired/used links — caller translates via error code.
   */
  /** Mint a 30-minute single-use link carrying this browser's form draft. */
  handoff: async (patientNumber: string, draft: Record<string, unknown>): Promise<{ url: string; expiresInMinutes: number }> => {
    const response = await api.post('/patient-links/handoff', { patientNumber, draft })
    return response.data.data
  },
  exchange: async (token: string): Promise<PatientLinkExchange> => {
    const response = await api.post('/patient-links/exchange', { token })
    return response.data.data
  },
}

// Document upload
export interface PdfUploadResponse {
  filename: string
  storageKey: string
  bucket: string
  originalName: string
}

export const documentService = {
  // Upload PDF to GCS with PXXXX_upload_N.pdf naming (or PXXXX_guardian.pdf for guardian documents)
  uploadPdf: async (file: File, patientNumber: string, uploadIndex: number | string): Promise<ApiResponse<PdfUploadResponse>> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('patientNumber', patientNumber)
    formData.append('uploadIndex', uploadIndex.toString())

    // Pass patient number as query param so the request logger can prefix logs (multer
    // hasn't parsed the FormData body yet when the global request logger runs)
    const response = await api.post(
      `/yousign/upload-pdf?pid=${encodeURIComponent(patientNumber)}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data
  },

  /**
   * Upload step (2026-10-16): stored at once with what the patient says it is, sent on "Weiter"
   * (finalizeUploads) as one PDF per document type.
   */
  uploadStaged: async (file: File, patientNumber: string, documentType: string, onProgress?: (percent: number) => void): Promise<StagedUpload> => {
    const formData = new FormData()
    formData.append('patientNumber', patientNumber)
    formData.append('uploadIndex', 'step')
    formData.append('staged', '1')
    formData.append('documentType', documentType)
    formData.append('file', file)
    const res = await api.post<ApiResponse<StagedUpload>>(`/yousign/upload-pdf?pid=${encodeURIComponent(patientNumber)}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: e => { if (onProgress && e.total) onProgress(Math.min(99, Math.round((e.loaded / e.total) * 100))) },
    })
    return res.data.data!
  },
  /** Take back a file that was not sent yet */
  removeStaged: async (patientNumber: string, id: string): Promise<void> => {
    await api.delete(`/yousign/uploads/${encodeURIComponent(id)}?pid=${encodeURIComponent(patientNumber)}`)
  },
  /** "Weiter": the files of this visit go to the practice; returns the documents they went into */
  finalizeUploads: async (patientNumber: string, ids: string[]): Promise<{ submittedAt: string; documents: { id: string; filename: string; documentType: string }[]; failedTypes: string[] }> => {
    const res = await api.post<ApiResponse<{ submittedAt: string; documents: { id: string; filename: string; documentType: string }[]; failedTypes: string[] }>>(
      `/yousign/uploads/finalize?pid=${encodeURIComponent(patientNumber)}`, { patientNumber, ids })
    return res.data.data!
  },
}

export interface StagedUpload {
  id: string
  filename: string
  storageKey: string
  originalName: string
  documentType: string
  sizeBytes: number
  createdAt: string
  pending: boolean
}

// Yousign endpoints
export interface ReadOnlyTextField {
  label: string
  text: string
}

export interface YousignCreateParams {
  firstName: string
  lastName: string
  phoneNumber: string
  email?: string
  patientName?: string
  patientNumber?: string
  birthPlace?: string
  gender?: 'male' | 'female' | 'diverse'
  birthdate?: string
  street?: string
  zip?: string
  city?: string
  country?: string
  readOnlyTextFields?: ReadOnlyTextField[]
  /** Change-detection hash — lets the server reuse a live SR instead of creating a paid duplicate */
  dataHash?: string
  /** Psychotherapy variant: the answers printed on its documents (the server renders them) */
  pt?: { insuranceType: 'public' | 'private' | 'none'; answers: Record<string, unknown> }
}

export interface YousignDocument {
  id: string
  nature: string
}

export interface YousignCreateResponse {
  signatureRequestId: string
  signerId: string
  patientId?: string
  status?: string
  /** Present when the server reused an existing (already activated) SR */
  documents?: YousignDocument[]
  /** Psychotherapy variant: contract key → document id (the request mixes template and uploaded documents) */
  documentMap?: Record<string, string>
  reused?: boolean
}

export interface YousignActivateResponse {
  signatureRequestId: string
  status: string
  documents: YousignDocument[]
}

export const yousignService = {
  create: async (params: YousignCreateParams): Promise<ApiResponse<YousignCreateResponse>> => {
    logger.log('[Yousign API] POST /yousign/create', params)
    const response = await api.post('/yousign/create', params)
    logger.log('[Yousign API] Response:', response.data)
    return response.data
  },

  activate: async (signatureRequestId: string): Promise<ApiResponse<YousignActivateResponse>> => {
    logger.log('[Yousign API] POST /yousign/activate', { signatureRequestId })
    const response = await api.post('/yousign/activate', { signatureRequestId })
    logger.log('[Yousign API] Response:', response.data)
    return response.data
  },

  sendOtp: async (signatureRequestId: string, signerId: string): Promise<ApiResponse<{ message: string }>> => {
    logger.log('[Yousign API] POST /yousign/send-otp', { signatureRequestId, signerId })
    const response = await api.post('/yousign/send-otp', { signatureRequestId, signerId })
    logger.log('[Yousign API] Response:', response.data)
    return response.data
  },

  // The signer IP is derived server-side from proxy headers
  sign: async (signatureRequestId: string, signerId: string, otp: string): Promise<ApiResponse<{ signerId: string; status: string; signedAt?: string }>> => {
    logger.log('[Yousign API] POST /yousign/sign', { signatureRequestId, signerId, otp: '******' })
    const response = await api.post('/yousign/sign', { signatureRequestId, signerId, otp })
    logger.log('[Yousign API] Response:', response.data)
    return response.data
  },

  downloadDocument: async (signatureRequestId: string, documentId: string): Promise<Blob> => {
    logger.log('[Yousign API] GET /yousign/documents/.../download', { signatureRequestId, documentId })
    const response = await api.get(`/yousign/documents/${signatureRequestId}/${documentId}/download`, {
      responseType: 'blob',
    })
    logger.log('[Yousign API] Response: Document blob received, size:', response.data.size)
    return response.data
  },

  // Store signed document and audit trail
  storeSignedDocument: async (signatureRequestId: string, patientId: string): Promise<ApiResponse<{ signedDocuments: unknown[]; auditTrail: unknown }>> => {
    logger.log('[Yousign API] POST /yousign/store-signed/...', { signatureRequestId, patientId })
    const response = await api.post(`/yousign/store-signed/${signatureRequestId}`, { patientId })
    logger.log('[Yousign API] Response:', response.data)
    return response.data
  },
}

// Phone cross-check against the number PsyCloud has on file (warn-only, Step 2)
export interface PhoneCheckResult {
  available: boolean
  match?: boolean
  maskedReference?: string
}

export const phoneCheckService = {
  /**
   * Would the signing service accept this number, and can it get the SMS code? (server-side
   * libphonenumber rules). Fail-open: any error means "no objection".
   */
  validate: async (phoneNumber: string): Promise<{ valid: boolean; mobile: boolean | null }> => {
    try {
      const response = await api.post('/phone-check/validate', { phoneNumber }, { timeout: 5000 })
      return response.data?.data ?? { valid: true, mobile: null }
    } catch {
      return { valid: true, mobile: null }
    }
  },
  /** Fail-open: any error means "no reference available" — never blocks the flow */
  check: async (patientNumber: string, phoneNumber: string): Promise<PhoneCheckResult> => {
    try {
      const response = await api.post('/phone-check', { patientNumber, phoneNumber }, { timeout: 6000 })
      return response.data?.data ?? { available: false }
    } catch {
      return { available: false }
    }
  },
}

// Geocoding (address autocomplete + verification, Photon proxy)
export interface AddressSuggestion {
  label: string
  street: string
  housenumber: string
  postcode: string
  city: string
  state: string
  country: string
  countrycode: string
  lat: number
  lon: number
  type: string
}

export type AddressVerificationStatus = 'exact' | 'street' | 'city_only' | 'unverified'

export interface AddressVerifyResult {
  available: boolean
  status: AddressVerificationStatus
  match?: AddressSuggestion
}

export const geocodeService = {
  autocomplete: async (q: string, country: string, signal?: AbortSignal): Promise<{ available: boolean; suggestions: AddressSuggestion[] }> => {
    const response = await api.get('/geocode/autocomplete', { params: { q, country }, signal })
    return response.data.data
  },

  verify: async (payload: { address: string; postalCode: string; city: string; country?: string }): Promise<AddressVerifyResult> => {
    const response = await api.post('/geocode/verify', payload)
    return response.data.data
  },
}

// SEPA — GoCardless Drop-in flow (proxied via our backend; see docs/sepa-dropin.md)
export interface SepaDropinFlow {
  available: boolean
  status?: 'created' | 'already_active'
  billingRequestFlowId?: string
  environment?: 'live' | 'sandbox'
  scheme?: string
  authorizationUrl?: string
  mandateId?: string
  reason?: string
}

export const sepaService = {
  getDropinFlow: async (patientNumber: string): Promise<SepaDropinFlow> => {
    const response = await api.get('/sepa/dropin-flow', { params: { pid: patientNumber } })
    return response.data.data
  },
  /** The mandate is in place as seen in the page — marks the stored submission (best-effort). */
  confirmMandate: async (patientNumber: string, via: 'dropin' | 'already_active'): Promise<void> => {
    await api.post('/submissions/sepa-mandate', { patientNumber, via })
  },
}

export interface CardSetupSession {
  available: boolean
  embedUrl?: string
  hostedUrl?: string
  dropinOrigin?: string
  environment?: 'test' | 'live'
  reason?: string
}

// Card-save drop-in (Stripe via the invoice system). The org API token stays
// server-side — this only ever sees the minted embed URL.
export const cardSetupService = {
  createSession: async (patientNumber: string): Promise<CardSetupSession> => {
    const response = await api.post('/card-setup/session', { patientNumber })
    return response.data.data
  },
}

// Settings service - public endpoints for maintenance/test mode
export const settingsService = {
  getPublicSettings: async (): Promise<{ maintenanceMode: boolean; hasMaintenanceBypassPassword: boolean; hasTestModePassword: boolean }> => {
    const response = await api.get('/settings/public')
    return response.data.data
  },

  verifyPassword: async (type: 'maintenance_bypass' | 'test_mode', password: string): Promise<boolean> => {
    const response = await api.post('/settings/verify', { type, password })
    return response.data.data.valid
  },
}

// Submission service - stores submissions in the database
export interface SubmissionResponse {
  id: string
  patientNumber: string
  submittedAt: string
  webhookSent?: boolean
  duplicate?: boolean
}

// Psychotherapy variant (server routes/vertraege.ts) — on the patient session of the magic link
export interface PtUploadInfo { id: string; kind: 'ptv11' | 'absageprotokoll'; name: string; size: number }

export const vertraegeService = {
  upload: async (kind: 'ptv11' | 'absageprotokoll', file: File): Promise<PtUploadInfo> => {
    const form = new FormData()
    form.append('kind', kind)
    form.append('file', file)
    const res = await api.post<ApiResponse<PtUploadInfo>>('/vertraege/uploads', form, { headers: { 'Content-Type': 'multipart/form-data' } })
    return res.data.data!
  },
  removeUpload: async (id: string): Promise<void> => {
    await api.delete(`/vertraege/uploads/${encodeURIComponent(id)}`)
  },
  stop: async (dateofbirth: string): Promise<void> => {
    await api.post('/vertraege/stop', { reason: 'under_18', dateofbirth })
  },
  complete: async (insuranceType: string, answers: Record<string, unknown>): Promise<{ status: string; id: string }> => {
    const res = await api.post<ApiResponse<{ status: string; id: string }>>('/vertraege/complete', { insuranceType, answers })
    return res.data.data!
  },
}

export const submissionService = {
  /** Whether this patient already has a stored submission (returning after the SEPA redirect). */
  getStatus: async (patientNumber: string): Promise<{ returned: boolean }> => {
    const response = await api.get(`/submissions/status/${encodeURIComponent(patientNumber)}`)
    return response.data.data
  },
  // Log submission start (patient number entered)
  logStart: async (patientNumber: string): Promise<void> => {
    try {
      await api.post('/submissions/start', { patientNumber })
    } catch {
      // Silently fail - this is just logging
    }
  },

  // Store submission in the database
  create: async (data: {
    patientNumber: string
    phoneNumber: string
    personalData: {
      gender: string
      firstName: string
      lastName: string
      email: string
      birthDate: string
      birthPlace: string
      codiceFiscale?: string
      codiceFiscaleAbsent?: boolean
      generalPractitionerName?: string
      generalPractitionerPhone?: string
      generalPractitionerEmail?: string
      idDocumentType?: string
      idDocumentNumber?: string
      idDocumentIssuedBy?: string
      idDocumentIssuedOn?: string
    }
    addressData: {
      address: string
      street?: string
      houseNumber?: string
      noHouseNumber?: boolean
      addressLine2?: string
      city: string
      state: string
      province?: string
      postalCode: string
      country: string
      domicileSameAsResidence?: boolean
      domicileAddress?: string
      domicileCity?: string
      domicileState?: string
      domicilePostalCode?: string
      domicileCountry?: string
    }
    insuranceData?: {
      insuranceType: string
      insuranceCountry: string
      insuranceProvider: string
      insuranceProviderOther: string
    }
    guardianData?: {
      hasGuardian: boolean
      guardianName: string
      guardianAddress: string
      guardianType?: string
      guardianFirstName?: string
      guardianLastName?: string
      guardianBirthDate?: string
      guardianBirthPlace?: string
      guardianTaxCode?: string
      guardianPhone?: string
      guardianEmail?: string
      guardianStreet?: string
      guardianHouseNumber?: string
      guardianPostalCode?: string
      guardianCity?: string
      guardianScope: string
      guardianDocumentFilename: string
      // Italy, patient under 18: the other parent, or why one signature is enough
      guardian2FirstName?: string
      guardian2LastName?: string
      guardian2Phone?: string
      guardian2Email?: string
      guardianSingleSignatureReason?: string
    }
    consents: {
      treatment_contract: boolean
      privacy_policy: boolean
      billing_consent: boolean
    }
    italyConsents?: {
      healthDataAccess: boolean
      marketing: boolean
      postVisitSurvey: boolean
      unsignedReportEmail: boolean
      televisitRecording: boolean
      termsPrivacy?: boolean
    }
    sepaOptOut: boolean
    sepaDropinCompleted?: boolean
    sepaVariant?: 'dropin' | 'redirect'
    sepaOptOutVariant?: 'control' | 'two_step'
    sepaOptOutReason?: string
    cardSaved?: boolean
    cardOptOut?: boolean
    addressValidation?: { status: string; normalized?: string; lat?: number; lon?: number } | null
    yousignRequestId: string | null
    yousignSignerId: string | null
    uploadedDocuments: { id: string; filename: string; documentType?: string }[]
    webhookSent?: boolean
  }): Promise<ApiResponse<SubmissionResponse>> => {
    const response = await api.post('/submissions', data)
    return response.data
  },
}

export default api

// ── Informed consent (standalone /informed-consent page) ─────────────────────
// Its own session header: a consent session is never a patient session.
export interface ConsentRequestSummary {
  id: string
  status: 'created' | 'opened' | 'sr_created' | 'signed' | 'stored' | 'canceled' | 'expired'
  kind: 'general' | 'medication' | 'confidentiality_waiver' | 'confidentiality_waiver_representative'
  templateName: string
  locale: string
  patient: { patientNumber: string; firstName: string; lastName: string; phoneMasked: string }
  // Schweigepflichtentbindung only
  relatedPerson?: { firstName: string; lastName: string; relationship: string } | null
  signer?: { role: 'patient' | 'legal_custodian' | 'authorized_representative'; firstName: string; lastName: string; phoneMasked: string }
  requestedPurposes?: string[]
  modeKeys?: string[]
  colleague: { name: string; title: string | null; role: string } | null
  medication: string | null
  dosage: string | null
  consents: { key: string; text: string; optional: boolean; label: string | null }[]
  /** One required confirmation for all consents of the document (template [consent-all: …]) */
  consentAll?: string | null
  given: Record<string, boolean>
  signedAt: string | null
  stored: boolean
  sandbox: boolean
  expiresAt: string
}

const consentHeaders = (sessionToken: string) => ({ headers: { 'X-Consent-Token': sessionToken } })

export const consentService = {
  exchange: async (token: string): Promise<{ sessionToken: string; organizationSlug: string; request: ConsentRequestSummary }> => {
    const res = await api.post<ApiResponse<{ sessionToken: string; organizationSlug: string; request: ConsentRequestSummary }>>('/consent/exchange', { token })
    return res.data.data!
  },
  me: async (sessionToken: string): Promise<ConsentRequestSummary> => {
    const res = await api.get<ApiResponse<ConsentRequestSummary>>('/consent/me', consentHeaders(sessionToken))
    return res.data.data!
  },
  document: async (sessionToken: string): Promise<Blob> => {
    const res = await api.get('/consent/document', { ...consentHeaders(sessionToken), responseType: 'blob' })
    return res.data as Blob
  },
  sendOtp: async (sessionToken: string, consents: Record<string, boolean>): Promise<{ phoneMasked: string; sandbox: boolean }> => {
    const res = await api.post<ApiResponse<{ phoneMasked: string; sandbox: boolean }>>('/consent/send-otp', { consents }, consentHeaders(sessionToken))
    return res.data.data!
  },
  sign: async (sessionToken: string, otp: string): Promise<{ status: string; signedAt: string }> => {
    const res = await api.post<ApiResponse<{ status: string; signedAt: string }>>('/consent/sign', { otp }, consentHeaders(sessionToken))
    return res.data.data!
  },
}

// ── Document upload link (standalone /upload page) ──────────────────────────
// Staff send the patient a link; the patient uploads documents into their own file.
export interface UploadLinkFileInfo { id: string; originalName: string; sizeBytes: number; documentType?: string | null; createdAt: string; pending?: boolean; submittedAt?: string | null }
export interface UploadLinkSummary {
  patientNumber: string
  firstName: string | null
  message: string | null
  /** Documents staff asked for (data/documentTypes.ts), shown as a checklist */
  requestedTypes?: string[]
  /** The requested ones no file has arrived for yet */
  missingTypes?: string[]
  /** When the current request was made: older files do not count for it */
  requestedAt?: string | null
  locale: string
  expiresAt: string
  uploadCount: number
  maxUploads: number
  remaining: number
  maxBytes: number
  /** uploaded, not sent yet ("Einsenden") */
  pendingCount?: number
  lastSubmittedAt?: string | null
  files: UploadLinkFileInfo[]
}

const uploadHeaders = (sessionToken: string) => ({ 'X-Upload-Token': sessionToken })

export const uploadLinkService = {
  exchange: async (token: string): Promise<{ sessionToken: string; organizationSlug: string; link: UploadLinkSummary }> => {
    const res = await api.post<ApiResponse<{ sessionToken: string; organizationSlug: string; link: UploadLinkSummary }>>('/upload-links/exchange', { token })
    return res.data.data!
  },
  me: async (sessionToken: string): Promise<UploadLinkSummary> => {
    const res = await api.get<ApiResponse<UploadLinkSummary>>('/upload-links/me', { headers: uploadHeaders(sessionToken) })
    return res.data.data!
  },
  uploadFile: async (sessionToken: string, file: File, documentType: string | null, onProgress?: (percent: number) => void): Promise<{ file: UploadLinkFileInfo; uploadCount: number; remaining: number; missingTypes?: string[] }> => {
    const formData = new FormData()
    if (documentType) formData.append('documentType', documentType)
    formData.append('file', file)
    const res = await api.post<ApiResponse<{ file: UploadLinkFileInfo; uploadCount: number; remaining: number; missingTypes?: string[] }>>('/upload-links/files', formData, {
      headers: { ...uploadHeaders(sessionToken), 'Content-Type': 'multipart/form-data' },
      onUploadProgress: e => { if (onProgress && e.total) onProgress(Math.min(99, Math.round((e.loaded / e.total) * 100))) },
    })
    return res.data.data!
  },
  /** Take back a file that was not sent yet */
  removeFile: async (sessionToken: string, fileId: string): Promise<UploadLinkSummary> => {
    const res = await api.delete<ApiResponse<UploadLinkSummary>>(`/upload-links/files/${encodeURIComponent(fileId)}`, { headers: uploadHeaders(sessionToken) })
    return res.data.data!
  },
  /** "Einsenden": one document per type to the practice */
  submit: async (sessionToken: string, opts: { stampSignatureConfirmed?: boolean } = {}): Promise<{ submittedAt: string; failedTypes: string[]; link: UploadLinkSummary }> => {
    const res = await api.post<ApiResponse<{ submittedAt: string; failedTypes: string[]; link: UploadLinkSummary }>>('/upload-links/submit', opts, { headers: uploadHeaders(sessionToken) })
    return res.data.data!
  },
}

// ── External-document signing (standalone /sign page) ────────────────────────
// A clinician signs a finished PDF another system produced. Own session header.
export interface SigningRequestSummary {
  id: string
  status: 'created' | 'opened' | 'sr_created' | 'signed' | 'stored' | 'canceled' | 'expired'
  title: string | null
  locale: string
  signer: { firstName: string; lastName: string }
  authentication: 'email_otp' | 'sms_otp' | 'none'
  destinationMasked: string | null
  hasSignatureImage: boolean
  pages: number
  signaturePages: number[]
  signedAt: string | null
  stored: boolean
  sandbox: boolean
  expiresAt: string
}

const signingHeaders = (sessionToken: string) => ({ headers: { 'X-Signing-Token': sessionToken } })

export const signingService = {
  exchange: async (token: string): Promise<{ sessionToken: string; organizationSlug: string; request: SigningRequestSummary }> => {
    const res = await api.post<ApiResponse<{ sessionToken: string; organizationSlug: string; request: SigningRequestSummary }>>('/signing/exchange', { token })
    return res.data.data!
  },
  me: async (sessionToken: string): Promise<SigningRequestSummary> => {
    const res = await api.get<ApiResponse<SigningRequestSummary>>('/signing/me', signingHeaders(sessionToken))
    return res.data.data!
  },
  document: async (sessionToken: string): Promise<Blob> => {
    const res = await api.get('/signing/document', { ...signingHeaders(sessionToken), responseType: 'blob' })
    return res.data as Blob
  },
  signatureImage: async (sessionToken: string): Promise<Blob> => {
    const res = await api.get('/signing/signature-image', { ...signingHeaders(sessionToken), responseType: 'blob' })
    return res.data as Blob
  },
  sendCode: async (sessionToken: string): Promise<{ channel: 'email' | 'sms'; destinationMasked: string | null; sandbox: boolean }> => {
    const res = await api.post<ApiResponse<{ channel: 'email' | 'sms'; destinationMasked: string | null; sandbox: boolean }>>('/signing/send-code', {}, signingHeaders(sessionToken))
    return res.data.data!
  },
  sign: async (sessionToken: string, otp: string | null): Promise<{ status: string; signedAt: string }> => {
    const res = await api.post<ApiResponse<{ status: string; signedAt: string }>>('/signing/sign', { otp, agreed: true }, signingHeaders(sessionToken))
    return res.data.data!
  },
}

// ── Bezugspersonenbogen (/bezugsperson?t=…, docs/bezugspersonenbogen.md) ─────
export interface RelatedPersonEntrySummary {
  id: string
  firstName: string
  lastName: string
  relationshipType: string
  createdAt: string
  waiver: { status: 'signed' | 'open' | 'not_started'; url: string | null } | null
}

export interface RelatedPersonLinkSummary {
  patientNumber: string
  firstName: string | null
  lastName: string | null
  locale: string
  expiresAt: string
  maxEntries: number
  remaining: number
  entries: RelatedPersonEntrySummary[]
}

export interface RelatedPersonAnswers {
  firstName: string
  lastName: string
  gender: 'female' | 'male' | 'diverse' | null
  relationshipType: string
  relationshipNote: string
  referencePerson: 'yes' | 'no'
  scope: string
  phone: string
  email: string
  street: string
  houseNumber: string
  zipCode: string
  city: string
  state: string
  country: string
  confidentialityWaiver: 'yes' | 'no'
  contactAgreed: 'yes' | 'no'
  privacyAccepted: boolean
}

export interface RelatedPersonSubmitResult {
  entryId: string
  waiver: { status: 'ready' | 'already_signed' | 'not_started'; url: string | null; phoneMasked?: string | null } | null
  link: RelatedPersonLinkSummary
}

const relatedPersonHeaders = (sessionToken: string) => ({ headers: { 'X-Related-Person-Token': sessionToken } })

export const relatedPersonService = {
  exchange: async (token: string): Promise<{ sessionToken: string; organizationSlug: string; link: RelatedPersonLinkSummary }> => {
    const res = await api.post<ApiResponse<{ sessionToken: string; organizationSlug: string; link: RelatedPersonLinkSummary }>>('/related-person/exchange', { token })
    return res.data.data!
  },
  me: async (sessionToken: string): Promise<RelatedPersonLinkSummary> => {
    const res = await api.get<ApiResponse<RelatedPersonLinkSummary>>('/related-person/me', relatedPersonHeaders(sessionToken))
    return res.data.data!
  },
  submit: async (sessionToken: string, answers: RelatedPersonAnswers): Promise<RelatedPersonSubmitResult> => {
    const res = await api.post<ApiResponse<RelatedPersonSubmitResult>>('/related-person/entries', { answers }, relatedPersonHeaders(sessionToken))
    return res.data.data!
  },
}
